import { useEffect, useRef } from 'react';
import './Galaxy.css';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function lerp(from, to, t) {
  return from + (to - from) * t;
}

function randomIn(min, max) {
  return Math.random() * (max - min) + min;
}

export default function Galaxy({
  focal = [0.5, 0.5],
  rotation = [1.0, 0.0],
  starSpeed = 0.5,
  density = 0.9,
  hueShift = 272,
  disableAnimation = false,
  speed = 1.0,
  mouseInteraction = true,
  glowIntensity = 0.16,
  saturation = 0.16,
  mouseRepulsion = true,
  repulsionStrength = 2,
  twinkleIntensity = 0.22,
  rotationSpeed = 0.1,
  autoCenterRepulsion = 0,
  transparent = true,
  ...rest
}) {
  const containerRef = useRef(null);
  const configRef = useRef({
    focal,
    rotation,
    starSpeed,
    density,
    hueShift,
    disableAnimation,
    speed,
    mouseInteraction,
    glowIntensity,
    saturation,
    mouseRepulsion,
    repulsionStrength,
    twinkleIntensity,
    rotationSpeed,
    autoCenterRepulsion,
    transparent,
  });

  useEffect(() => {
    configRef.current = {
      focal,
      rotation,
      starSpeed,
      density,
      hueShift,
      disableAnimation,
      speed,
      mouseInteraction,
      glowIntensity,
      saturation,
      mouseRepulsion,
      repulsionStrength,
      twinkleIntensity,
      rotationSpeed,
      autoCenterRepulsion,
      transparent,
    };
  }, [
    focal,
    rotation,
    starSpeed,
    density,
    hueShift,
    disableAnimation,
    speed,
    mouseInteraction,
    glowIntensity,
    saturation,
    mouseRepulsion,
    repulsionStrength,
    twinkleIntensity,
    rotationSpeed,
    autoCenterRepulsion,
    transparent,
  ]);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const container = containerRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { alpha: true });

    if (!ctx) {
      return undefined;
    }

    container.appendChild(canvas);

    const state = {
      stars: [],
      width: 0,
      height: 0,
      dpr: 1,
      mouseTargetX: 0.5,
      mouseTargetY: 0.5,
      mouseX: 0.5,
      mouseY: 0.5,
      mouseActive: 0,
      currentSpeed: configRef.current.speed,
      worldTime: 0,
      lastNow: 0,
      rafId: null,
    };

    const createStar = () => ({
      x: randomIn(-1, 1),
      y: randomIn(-1, 1),
      z: randomIn(0.05, 1),
      size: randomIn(0.4, 1.35),
      alpha: randomIn(0.24, 0.9),
      twinkleOffset: randomIn(0, Math.PI * 2),
      hueJitter: randomIn(-10, 10),
    });

    const resetStar = (star) => {
      star.x = randomIn(-1, 1);
      star.y = randomIn(-1, 1);
      star.z = randomIn(0.75, 1);
      star.size = randomIn(0.4, 1.35);
      star.alpha = randomIn(0.24, 0.9);
      star.twinkleOffset = randomIn(0, Math.PI * 2);
      star.hueJitter = randomIn(-10, 10);
    };

    const rebuildStars = () => {
      const cfg = configRef.current;
      const starCount = Math.round(clamp(220 * cfg.density, 100, 820));
      state.stars = Array.from({ length: starCount }, createStar);
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      state.width = Math.max(1, rect.width);
      state.height = Math.max(1, rect.height);
      state.dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.floor(state.width * state.dpr);
      canvas.height = Math.floor(state.height * state.dpr);
      canvas.style.width = `${state.width}px`;
      canvas.style.height = `${state.height}px`;

      ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
      rebuildStars();
    };

    const handleMouseMove = (event) => {
      const rect = container.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return;
      }
      state.mouseTargetX = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      state.mouseTargetY = clamp((event.clientY - rect.top) / rect.height, 0, 1);
      state.mouseActive = 1;
    };

    const handleMouseLeave = () => {
      state.mouseActive = 0;
    };

    const draw = (now) => {
      const cfg = configRef.current;
      const nowSeconds = now * 0.001;
      const prev = state.lastNow || nowSeconds;
      const dt = clamp(nowSeconds - prev, 0, 0.05);
      state.lastNow = nowSeconds;

      const targetSpeed = cfg.disableAnimation ? 0 : clamp(cfg.speed, 0, 5);
      const speedFollow = 1 - Math.exp(-dt * 10);
      state.currentSpeed = lerp(state.currentSpeed, targetSpeed, speedFollow);
      state.worldTime += dt * state.currentSpeed;

      const mouseLerp = 1 - Math.exp(-dt * 8);
      state.mouseX = lerp(state.mouseX, state.mouseTargetX, mouseLerp);
      state.mouseY = lerp(state.mouseY, state.mouseTargetY, mouseLerp);

      if (cfg.transparent) {
        ctx.clearRect(0, 0, state.width, state.height);
      } else {
        ctx.fillStyle = 'rgb(0, 0, 0)';
        ctx.fillRect(0, 0, state.width, state.height);
      }

      const focalX = clamp(cfg.focal?.[0] ?? 0.5, 0, 1);
      const focalY = clamp(cfg.focal?.[1] ?? 0.5, 0, 1);
      const centerX = state.width * focalX;
      const centerY = state.height * focalY;
      const baseScale = Math.min(state.width, state.height) * 0.95;

      const rotationX = cfg.rotation?.[0] ?? 1.0;
      const rotationY = cfg.rotation?.[1] ?? 0.0;
      const rotMag = Math.max(Math.hypot(rotationX, rotationY), 0.0001);
      const rotNX = rotationX / rotMag;
      const rotNY = rotationY / rotMag;
      const autoAngle = state.worldTime * cfg.rotationSpeed * 0.25;
      const cosA = Math.cos(autoAngle);
      const sinA = Math.sin(autoAngle);

      const mouseOffsetX = (state.mouseX - 0.5) * 2;
      const mouseOffsetY = (state.mouseY - 0.5) * 2;
      const mousePower = state.mouseActive * (cfg.mouseInteraction ? 1 : 0);

      const hueBase = 272 + (cfg.hueShift - 272) * 0.25;
      const satBase = clamp(24 + cfg.saturation * 56, 12, 78);
      const glowBase = clamp(cfg.glowIntensity, 0, 0.45);

      for (let i = 0; i < state.stars.length; i += 1) {
        const star = state.stars[i];

        const forward = dt * cfg.starSpeed * (0.2 + star.size * 0.35) * state.currentSpeed;
        star.z -= forward;
        if (star.z <= 0.02) {
          resetStar(star);
        }

        let x = star.x;
        let y = star.y;

        if (cfg.autoCenterRepulsion > 0) {
          const dist = Math.max(Math.hypot(x, y), 0.05);
          const push = (cfg.autoCenterRepulsion * dt * 0.08) / (dist + 0.2);
          x += (x / dist) * push;
          y += (y / dist) * push;
        } else if (cfg.mouseRepulsion && mousePower > 0) {
          const mx = mouseOffsetX * 0.9;
          const my = mouseOffsetY * -0.9;
          const rx = x - mx;
          const ry = y - my;
          const dist = Math.max(Math.hypot(rx, ry), 0.08);
          const push = (cfg.repulsionStrength * mousePower * dt * 0.24) / (dist + 0.25);
          x += (rx / dist) * push;
          y += (ry / dist) * push;
        }

        const rx = x * cosA - y * sinA;
        const ry = x * sinA + y * cosA;
        const rotX = rx * rotNX - ry * rotNY;
        const rotY = rx * rotNY + ry * rotNX;

        const depth = 1.08 - star.z;
        const perspective = baseScale / (star.z * 1.9 + 0.28);
        const px = centerX + rotX * perspective;
        const py = centerY + rotY * perspective;
        const radius = clamp((0.4 + depth * 1.95) * star.size, 0.3, 3.9);

        if (px < -20 || px > state.width + 20 || py < -20 || py > state.height + 20) {
          resetStar(star);
          continue;
        }

        const twinkle = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(state.worldTime * (1.2 + cfg.twinkleIntensity * 1.8) + star.twinkleOffset));
        const alpha = clamp(star.alpha * twinkle * (0.38 + depth * 0.6), 0.1, 0.82);
        const hue = hueBase + star.hueJitter;
        const light = clamp(62 + depth * 16, 52, 82);

        ctx.shadowBlur = 2.4 + glowBase * 12;
        ctx.shadowColor = `hsla(${hue}, ${satBase}%, ${light}%, ${alpha * 0.52})`;
        ctx.fillStyle = `hsla(${hue}, ${satBase}%, ${light}%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.shadowBlur = 0;
      state.rafId = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);

    if (configRef.current.mouseInteraction) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);
    }

    state.rafId = requestAnimationFrame(draw);

    return () => {
      if (state.rafId) {
        cancelAnimationFrame(state.rafId);
      }
      window.removeEventListener('resize', resize);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      if (canvas.parentNode === container) {
        container.removeChild(canvas);
      }
    };
  }, []);

  return <div ref={containerRef} className="galaxy-container" {...rest} />;
}
